import { useMemo } from 'react';
import { Minimize2, XCircle, Sparkles, RefreshCw, SkipForward, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

import { LiveActivityFeed } from '../components/LiveActivityFeed';
import { ButtonTemplate } from '../../../components/ui/buttons/ButtonTemplate';
import { useCancelBatchMutation } from '../api/parserImportApi';

const fmtDuration = (seconds) => {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}m ${s}s`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `${h}h ${m}m`;
};

const StatBadge = ({ Icon, label, value, className, testId }) => (
  <div className="flex items-center gap-2 px-3 py-2 rounded border border-gray-200 bg-white">
    <Icon size={16} className={className} />
    <div className="flex flex-col leading-tight">
      <span className="text-[10px] uppercase tracking-wider text-darkGrayColor">
        {label}
      </span>
      <span
        data-testid={testId}
        className={'text-base font-semibold ' + className}
      >
        {value ?? 0}
      </span>
    </div>
  </div>
);

// Circular progress SVG.
const RADIUS = 60;
const CIRC = 2 * Math.PI * RADIUS;
const CircularProgress = ({ percent, label }) => (
  <div className="relative flex items-center justify-center w-40 h-40">
    <svg width="160" height="160" viewBox="0 0 160 160">
      <circle
        cx="80"
        cy="80"
        r={RADIUS}
        stroke="#f5f5f5"
        strokeWidth="10"
        fill="none"
      />
      <circle
        cx="80"
        cy="80"
        r={RADIUS}
        stroke="#848cba"
        strokeWidth="10"
        fill="none"
        strokeDasharray={`${(percent / 100) * CIRC} ${CIRC}`}
        strokeLinecap="round"
        transform="rotate(-90 80 80)"
        style={{ transition: 'stroke-dasharray 400ms ease-out' }}
      />
    </svg>
    <div className="absolute flex flex-col items-center">
      <span className="text-3xl font-semibold text-blackColor">{percent}%</span>
      <span className="text-xs text-darkGrayColor">{label}</span>
    </div>
  </div>
);

// Step 3 — live progress: circle, counters, throughput, ETA, activity feed,
// cancel + run-in-background actions.
export const ProgressStep = ({ flow, token, batch, onMinimize }) => {
  const [cancelBatch, { isLoading: isCancelling }] = useCancelBatchMutation();

  const counts = batch?.counts || {};
  const total = counts.total ?? 0;
  const processed = counts.processed ?? 0;
  const percent = total ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  const status = batch?.status || 'pending';

  const recentItems = useMemo(
    () => (batch?.recent_items || []).slice(0, 5),
    [batch]
  );

  const handleCancel = async () => {
    if (!flow.batchId) return;
    try {
      await cancelBatch({ token, batchId: flow.batchId }).unwrap();
      toast.info('Cancelling…');
    } catch (e) {
      toast.error(e?.data?.message || 'Cancel failed');
    }
  };

  const isCancelling2 = status === 'cancelling' || isCancelling;

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <CircularProgress
            percent={percent}
            label={
              <>
                <span data-testid="progress-count-processed">{processed}</span>
                /
                <span data-testid="progress-count-total">{total}</span>
              </>
            }
          />
          <div className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-wider text-darkGrayColor">
              Status
            </span>
            <span
              data-testid="progress-batch-status"
              className={
                'text-lg font-semibold ' +
                (status === 'running'
                  ? 'text-purpleColor'
                  : status === 'cancelling'
                  ? 'text-orangeColor'
                  : 'text-blackColor')
              }
            >
              {status}
            </span>
            <span className="text-xs text-darkGrayColor mt-2">Throughput</span>
            <span
              data-testid="progress-throughput"
              className="text-sm font-medium text-blackColor"
            >
              {batch?.throughput_per_min != null
                ? `${Math.round(batch.throughput_per_min)} / min`
                : '—'}
            </span>
            <span className="text-xs text-darkGrayColor mt-2">ETA</span>
            <span
              data-testid="progress-eta"
              className="text-sm font-medium text-blackColor"
            >
              {fmtDuration(batch?.eta_seconds)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 self-start">
          <button
            type="button"
            data-testid="progress-run-background-button"
            onClick={onMinimize}
            className="flex items-center gap-1 px-3 py-1.5 text-sm rounded border border-gray-200 text-darkGrayColor hover:bg-lightGrayColor"
          >
            <Minimize2 size={14} />
            Run in background
          </button>
          <span data-testid="progress-cancel-button">
            <ButtonTemplate
              text={isCancelling2 ? 'Cancelling…' : 'Cancel'}
              icon={XCircle}
              isIconText={true}
              bgColor="bg-redColor border-redColor"
              disabled={isCancelling2 || status === 'cancelled' || status === 'completed'}
              handleClick={handleCancel}
            />
          </span>
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <StatBadge testId="progress-count-created" Icon={Sparkles} label="Created" value={counts.created} className="text-greenColor" />
        <StatBadge testId="progress-count-enriched" Icon={RefreshCw} label="Enriched" value={counts.enriched} className="text-purpleColor" />
        <StatBadge Icon={RefreshCw} label="Overwritten" value={counts.overwritten} className="text-orangeColor" />
        <StatBadge Icon={SkipForward} label="Skipped" value={counts.skipped} className="text-darkGrayColor" />
        <StatBadge testId="progress-count-failed" Icon={AlertTriangle} label="Failed" value={counts.failed} className="text-redColor" />
      </div>

      {/* Live feed */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase tracking-wider text-darkGrayColor">
            Recent activity
          </span>
          <span className="text-[10px] text-darkGrayColor animate-pulse">
            polling every 2s…
          </span>
        </div>
        <LiveActivityFeed items={recentItems} />
      </div>
    </div>
  );
};
